import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { DateTime } from 'luxon';

import Grid from '@mui/material/Grid';

// @mui/icons-material
import PhoneIcon from '@mui/icons-material/Phone';

// core components
import Card from '../md/components/Card/Card.jsx';
import CardHeader from '../md/components/Card/CardHeader.jsx';
import CardIcon from '../md/components/Card/CardIcon.jsx';
import CardBody from '../md/components/Card/CardBody.jsx';

import { updateReferral } from '../store/slices/referralsSlice';
import { setCaseIntendedClientId } from '../store/slices/intendedInfoSlice';
import { clearTaskAccomplishmentInfo } from '../store/slices/dashboardStatsSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import {
  createStdRequest,
  apiFetch,
  sortedDropdownItemLabels,
  isPractitionerType,
  isProprietorshipType,
} from '../utils/utils';

import DA from '../store/ReduxAccess';
import ReduxLoader from '../store/ReduxLoader';
import SocketAccess from '../comm/SocketAccess';

import KSelect from '../components/KSelect';
import NoteAssocGroup from '../components/NoteAssocGroup';
import NewNoteModal from '../components/NewNoteModal';
import CrmButton from '../components/CrmButton';
import KDatePicker from '../components/KDatePicker';
import CrmSnackbar from '../components/CrmSnackbar02';
import { searchableContactDataBuilder } from '../components/SearchableContactHierarchy';
import ReferralSelectionHierarchy from '../components/ReferralSelectionHierarchy';
import IcfReferrerFirmModal from '../components/IcfReferrerFirmModal';
import IcfConsultancyModal from '../components/IcfConsultancyModal';
import IcfPractitionerModal from '../components/IcfPractitionerModal';
import IcfOrgContactModal from '../components/IcfOrgContactModal';

function buildReferralApiStruct(referralId) {
  const referral = DA.getReferralById(referralId);
  return {
    ...referral,
    date: referral.date.toISO(),
    contactDate: referral.date.toISO(),
  };
}

function computeReceiverOptions() {
  const allUsers = DA.getAllFirmUsers();
  const pairs = allUsers.map((u) => {
    return { value: u.personId, label: u.name };
  });
  return pairs.sort((p1, p2) => (p1.name < p2.name ? -1 : p1.name > p2.name ? 1 : 0));
}

let renderIsResultOfWebSocketUpdate = false;

function ReferralPage(props) {
  const [rerenderValue, setRerenderValue] = useState(true);

  const rerenderFunc = () => {
    renderIsResultOfWebSocketUpdate = true;
    setRerenderValue(!rerenderValue); // toggle the state to force render
  };

  let searchableContactData = searchableContactDataBuilder.build();

  const commTypeDropdownOptions = sortedDropdownItemLabels('Referral Communication Type');
  const receiverDropdownOptions = computeReceiverOptions();
  const campaigns = DA.getAllCampaigns();
  const salesStageDropdownOptions = sortedDropdownItemLabels('Sales Stage');

  const computeCategoryForSourceType = (sourceType) => {
    /*
     * Examples:
     * Hospital -> Company / Organization
     * Govt/Private Agency -> Company / Organization
     * Family -> Word of Mouth
     * Other Word of Mouth -> Word of Mouth
     * Internet Search -> Internet Search
     * Facebook -> Social Media
     * Other Social Media -> Social Media
     * TV -> Media
     * Internet -> Media
     * Other Media -> Media
     * Marketing Event/Campaign -> Marketing Event/Campaign
     * Unknown -> Unknown
     */
    if (sourceType === 'Unknown') {
      return 'Unknown';
    }

    if (isCompanyOrgSourceType(sourceType)) {
      return 'Company / Organization';
    }

    if (isWordOfMouthSourceType(sourceType)) {
      return 'Word of Mouth';
    }

    if (isSocialMediaSourceType(sourceType)) {
      return 'Social Media';
    }

    if (isMediaSourceType(sourceType)) {
      return 'Media';
    }

    if (sourceType === 'Internet Search') {
      return 'Internet Search';
    }

    if (sourceType === 'Marketing Event/Campaign') {
      return 'Marketing Event/Campaign';
    }

    return 'Unknown';
  };

  function isCompanyOrgSourceType(sourceType) {
    const sourceTypes = searchableContactData.orgTypes.map((cat) => cat.name);
    return sourceTypes.indexOf(sourceType) >= 0;
  }

  function isWordOfMouthSourceType(sourceType) {
    return (
      sourceType === 'Family' ||
      sourceType === 'Friends' ||
      sourceType === 'Staff' ||
      sourceType === 'Clients/Alumni' ||
      sourceType === 'Contacts of Clients/Alumni' ||
      sourceType === 'Other Word of Mouth'
    );
  }

  function isSocialMediaSourceType(sourceType) {
    return (
      sourceType === 'Facebook' ||
      sourceType === 'LinkedIn' ||
      sourceType === 'Twitter' ||
      sourceType === 'Instagram' ||
      sourceType === 'TikTok' ||
      sourceType === 'Other Social Media'
    );
  }

  function isMediaSourceType(sourceType) {
    return (
      sourceType === 'TV' ||
      sourceType === 'Radio' ||
      sourceType === 'Print' ||
      sourceType === 'Internet' ||
      sourceType === 'Other Media'
    );
  }

  const [redirect, setRedirect] = useState(null);
  const [shouldShowSnackbar, setShouldShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showNoteModal, setShowNoteModal] = useState(false);

  const referral = DA.getReferralById(props.referralId);
  const refDateIso = referral.date.toISO();

  const [dateIso, setDateIso] = useState(refDateIso);
  const [commType, setCommType] = useState(referral.communicationType);
  const [receiverId, setReceiverId] = useState(referral.receivingUserPersonId);
  const [campaignId, setCampaignId] = useState(referral.campaignId);
  const [salesStage, setSalesStage] = useState(referral.salesStage);

  const [shouldShowReferrerFirmModal, setShouldShowReferrerFirmModal] = useState(false);
  const [shouldShowProprietorshipModal, setShouldShowProprietorshipModal] = useState(false);
  const [shouldShowPractitionerModal, setShouldShowPractitionerModal] = useState(false);
  const [shouldShowContactModal, setShouldShowContactModal] = useState(false);

  if (refDateIso !== dateIso) {
    setDateIso(refDateIso);
  }
  if (referral.communicationType !== commType) {
    setCommType(referral.communicationType);
  }
  if (referral.receivingUserPersonId !== receiverId) {
    setReceiverId(referral.receivingUserPersonId);
  }
  if (referral.campaignId !== campaignId) {
    setCampaignId(referral.campaignId);
  }

  function computeReferralSourceValuesForHierarchy(referral) {
    let referrerType = referral.sourceType; // like 'Hospital', 'Private Practitioner', 'Govt/Private Agency', 'Family', or 'Other Social Media'
    const sourceCategory = computeCategoryForSourceType(referrerType); // like 'Company / Organization' or 'Social Media' or 'Internet Search'
    const referringOrgId = referral.referringOrgId;
    const referringPersonId = referral.referringPersonId;
    const sourceSpecies = referral.sourceSpecies;
    let wordOfMouthOption = '';
    let socialMediaOption = '';
    let mediaOption = '';
    let wasOther = false;
    if (sourceCategory !== 'Company / Organization') {
      if (referrerType.startsWith('Other ')) {
        referrerType = referrerType.substring(6);
        wasOther = true;
      }
      if (sourceCategory === 'Word of Mouth') {
        wordOfMouthOption = wasOther ? 'Other' : referrerType;
        referrerType = '';
      } else if (sourceCategory === 'Social Media') {
        socialMediaOption = wasOther ? 'Other' : referrerType;
        referrerType = '';
      } else if (sourceCategory === 'Media') {
        mediaOption = wasOther ? 'Other' : referrerType;
        referrerType = '';
      } else {
        referrerType = '';
      }
    }
    return {
      sourceCategory,
      referrerType,
      referringOrgId,
      referringPersonId,
      sourceSpecies,
      wordOfMouthOption,
      socialMediaOption,
      mediaOption,
    };
  }

  const {
    sourceCategory,
    referrerType,
    referringOrgId,
    referringPersonId,
    sourceSpecies,
    wordOfMouthOption,
    socialMediaOption,
    mediaOption,
  } = computeReferralSourceValuesForHierarchy(referral);

  const [chosenReferralCategory, setChosenReferralCategory] = useState(sourceCategory);
  const [chosenReferrerType, setChosenReferrerType] = useState(referrerType);
  const [chosenReferrerOrgId, setChosenReferrerOrgId] = useState(referringOrgId);
  const [chosenReferrerContactId, setChosenReferrerContactId] = useState(referringPersonId);
  const [referralOptionalSpecies, setReferralOptionalSpecies] = useState(sourceSpecies);
  const [chosenWordOfMouthOption, setChosenWordOfMouthOption] = useState(wordOfMouthOption);
  const [chosenSocialMediaOption, setChosenSocialMediaOption] = useState(socialMediaOption);
  const [chosenMediaOption, setChosenMediaOption] = useState(mediaOption);
  if (renderIsResultOfWebSocketUpdate) {
    setChosenReferralCategory(sourceCategory);
    setChosenReferrerType(referrerType);
    setChosenReferrerOrgId(referringOrgId);
    setChosenReferrerContactId(referringPersonId);
    setReferralOptionalSpecies(sourceSpecies);
    setChosenWordOfMouthOption(wordOfMouthOption);
    setChosenSocialMediaOption(socialMediaOption);
    setChosenMediaOption(mediaOption);
  }
  let savedSourceCategory = sourceCategory;
  let savedReferrerType = referrerType;
  let savedReferrerOrgId = referringOrgId;
  let savedReferrerContactId = referringPersonId;
  let savedOptionalSpecies = sourceSpecies;
  let savedWordOfMouthOption = wordOfMouthOption;
  let savedSocialMediaOption = socialMediaOption;
  let savedMediaOption = mediaOption;

  if (referral.campaignId !== campaignId) {
    setCampaignId(referral.campaignId);
  }

  const registerCallbackStructForEffect = () => {
    const referralCallbackStruct = {
      kind: 'referral',
      pageName: 'ReferralPage',
      refreshFunc: () => rerenderFunc(),
    };
    SocketAccess.registerRefreshCallback(referralCallbackStruct);
    return () => SocketAccess.unregisterRefreshCallbacksForPage('ReferralPage');
  };

  useEffect(registerCallbackStructForEffect);

  const referree = DA.getPersonById(referral.referreeId);

  const notes = DA.getNotesAboutReferal(props.referralId);

  const patchReferralInDbAndRedux = (fieldName, newValue, snackbarLabel, apiStruct) => {
    const instructions = buildInstructions(fieldName, newValue, apiStruct);
    const uri = `/api/referral/${props.referralId}`;
    const request = createStdRequest(uri, 'PATCH', instructions);
    const actionOnGoodResponse = () => {
      // store revised state in Redux
      const reduxStruct = ReduxLoader.convertReferralApiStructIntoReduxStruct(apiStruct);
      props.updateReferral(reduxStruct);

      if (fieldName === 'referralSourceInfo') {
        updateSavedReferralSourceInfo(apiStruct);
      }

      showSnackbar(`${snackbarLabel} saved`);
    };
    apiFetch(null, request, actionOnGoodResponse);
  };

  const updateSavedReferralSourceInfo = (apiStruct) => {
    savedSourceCategory = chosenReferralCategory;
    savedReferrerType = chosenReferrerType;
    savedReferrerOrgId = chosenReferrerOrgId;
    savedReferrerContactId = chosenReferrerContactId;
    savedOptionalSpecies = referralOptionalSpecies;
    savedWordOfMouthOption = chosenWordOfMouthOption;
    savedSocialMediaOption = chosenSocialMediaOption;
    savedMediaOption = chosenMediaOption;
  };

  const buildInstructions = (quasiFieldName, newValue, apiStruct) => {
    if (quasiFieldName === 'referralSourceInfo') {
      // We have at least four fields to communicate, which we'll copy from the apiStruct
      return [
        { op: 'replace', path: '/sourceType', value: apiStruct.sourceType },
        { op: 'replace', path: '/referringOrgId', value: apiStruct.referringOrgId },
        { op: 'replace', path: '/referringPersonId', value: apiStruct.referringPersonId },
        { op: 'replace', path: '/sourceSpecies', value: apiStruct.sourceSpecies },
      ];
    } else {
      return [{ op: 'replace', path: `/${quasiFieldName}`, value: newValue }];
    }
  };

  function showSnackbar(message) {
    setSnackbarMessage(message);
    setShouldShowSnackbar(true);
  }

  const handleDateChange = (newDate) => {
    const newDateIso = newDate.toISO();
    const apiStruct = buildReferralApiStruct(props.referralId);
    apiStruct.contactDate = newDateIso;
    apiStruct.date = newDateIso;
    patchReferralInDbAndRedux('date', newDateIso, 'Referral Date', apiStruct);
    setDateIso(newDateIso);
  };

  const handleCommTypeChange = (event) => {
    const newCommType = event.target.value;
    const apiStruct = buildReferralApiStruct(props.referralId);
    apiStruct.communicationType = newCommType;
    patchReferralInDbAndRedux('communicationType', newCommType, 'Communication Type', apiStruct);
    setCommType(newCommType);
  };

  const hanldeReceiverChange = (event) => {
    const newReceiverId = event.target.value;
    const apiStruct = buildReferralApiStruct(props.referralId);
    apiStruct.receiverId = newReceiverId;
    apiStruct.receivingUserPersonId = newReceiverId;
    patchReferralInDbAndRedux('receiverId', newReceiverId, 'Received By', apiStruct);
    setReceiverId(newReceiverId);
  };

  const clearHierarchyValues = () => {
    setChosenReferrerType('');
    setChosenReferrerOrgId(0);
    setChosenReferrerContactId(0);
    setReferralOptionalSpecies('');
    setChosenWordOfMouthOption('');
    setChosenSocialMediaOption('');
    setChosenMediaOption('');
  };

  const handleReferralCategoryChange = (newCategory) => {
    clearHierarchyValues();
    setChosenReferralCategory(newCategory);
  };

  const handleReferrerTypeChange = (newValue) => {
    clearHierarchyValues();
    setChosenReferrerType(newValue);
  };

  const handleSelectCompany = (referrerType, referrerOrgId) => {
    clearHierarchyValues();
    setChosenReferrerType(referrerType);
    setChosenReferrerOrgId(referrerOrgId);
  };

  const handleSelectContact = (referrerType, referrerOrgId, personId) => {
    if (referrerOrgId) {
      clearHierarchyValues();
      setChosenReferrerType(referrerType);
      setChosenReferrerOrgId(referrerOrgId);
    }
    setChosenReferrerContactId(personId);
  };

  const handleWordOfMouthOptionChange = (newOption) => {
    setChosenWordOfMouthOption(newOption);
    setReferralOptionalSpecies('');
  };

  const handleSocialMediaOptionChange = (newOption) => {
    setChosenSocialMediaOption(newOption);
    setReferralOptionalSpecies('');
  };

  const handleMediaOptionChange = (newOption) => {
    setChosenMediaOption(newOption);
    setReferralOptionalSpecies('');
  };

  const forceShowingOfNewReferrerCompanyModal = (referrerType) => {
    setChosenReferrerType(referrerType);
    if (isProprietorshipType(referrerType)) {
      setShouldShowProprietorshipModal(true);
    } else if (isPractitionerType(referrerType)) {
      setShouldShowPractitionerModal(true);
    } else {
      setShouldShowReferrerFirmModal(true);
    }
  };

  const handleNewReferrerFirmSuccess = (visibleFirmType, visibleFirmId, contactId) => {
    searchableContactData = searchableContactDataBuilder.build();
    setChosenReferrerType(visibleFirmType);
    setChosenReferrerOrgId(visibleFirmId);
    setChosenReferrerContactId(contactId);
    setShouldShowReferrerFirmModal(false);
  };

  const handleNewConsultancySuccess = (visibleFirmType, visibleFirmId, contactId) => {
    // practically identical to handleNewReferrerFirmSuccess()
    searchableContactData = searchableContactDataBuilder.build();
    setChosenReferrerType(visibleFirmType);
    setChosenReferrerOrgId(visibleFirmId);
    setChosenReferrerContactId(contactId);
    setShouldShowProprietorshipModal(false);
  };

  const handleNewPractitionerSuccess = (visibleFirmType, visibleFirmId, contactId) => {
    // practically identical to handleNewReferrerFirmSuccess()
    searchableContactData = searchableContactDataBuilder.build();
    setChosenReferrerType(visibleFirmType);
    setChosenReferrerOrgId(visibleFirmId);
    setChosenReferrerContactId(contactId);
    setShouldShowPractitionerModal(false);
  };

  const handleNewOrgContactSuccess = (contactId) => {
    searchableContactData = searchableContactDataBuilder.build();
    setChosenReferrerContactId(contactId);
    setShouldShowContactModalStateVar(false);
  };

  const forceShowingOfNewContactModal = (referrerType, firmId) => {
    setChosenReferrerType(referrerType);
    setChosenReferrerOrgId(firmId);
    setShouldShowContactModal(true);
  };

  const handleCampaignChange = (event) => {
    const newCampaignId = event.target.value;
    const apiStruct = buildReferralApiStruct(props.referralId);
    apiStruct.campaignId = newCampaignId;
    patchReferralInDbAndRedux('campaignId', newCampaignId, 'Campaign', apiStruct);
    setCampaignId(newCampaignId);
  };

  const handleInitialSalesStageChange = (event) => {
    const newStage = event.target.value;
    const apiStruct = buildReferralApiStruct(props.referralId);
    apiStruct.salesStage = newStage;
    patchReferralInDbAndRedux('initialSalesStage', newStage, 'Initial Sales Stage', apiStruct);
    setSalesStage(newStage);
  };

  const cancelReferralSourceInfoChanges = () => {
    setChosenReferralCategory(savedSourceCategory);
    setChosenReferrerType(savedReferrerType);
    setChosenReferrerOrgId(savedReferrerOrgId);
    setChosenReferrerContactId(savedReferrerContactId);
    setReferralOptionalSpecies(savedOptionalSpecies);
    setChosenWordOfMouthOption(savedWordOfMouthOption);
    setChosenSocialMediaOption(savedSocialMediaOption);
    setChosenMediaOption(savedMediaOption);
  };

  const saveReferralSourceInfo = () => {
    const apiStruct = buildReferralApiStruct(props.referralId);

    // The next four are to avoid repeating them in most cases
    apiStruct.referringOrgId = 0;
    apiStruct.referringPersonId = 0;
    apiStruct.sourceSpecies = referralOptionalSpecies;

    if (chosenReferralCategory === 'Company / Organization') {
      apiStruct.sourceType = chosenReferrerType;
      apiStruct.referringOrgId = chosenReferrerOrgId;
      apiStruct.referringPersonId = chosenReferrerContactId;
    } else if (chosenReferralCategory === 'Word of Mouth') {
      apiStruct.sourceType = chosenWordOfMouthOption === 'Other' ? 'Other Word of Mouth' : chosenWordOfMouthOption;
    } else if (chosenReferralCategory === 'Social Media') {
      apiStruct.sourceType = chosenSocialMediaOption === 'Other' ? 'Other Social Media' : chosenSocialMediaOption;
    } else if (chosenReferralCategory === 'Media') {
      apiStruct.sourceType = chosenMediaOption === 'Other' ? 'Other Media' : chosenMediaOption;
    } else {
      apiStruct.sourceType = chosenReferralCategory;
      apiStruct.sourceSpecies = '';
    }
    patchReferralInDbAndRedux('referralSourceInfo', null, 'Referral Source Info', apiStruct);
  };

  function createNote(noteText) {
    const uri = '/api/note';
    const apiStruct = createNoteApiStruct(noteText);
    const request = createStdRequest(uri, 'POST', apiStruct);
    const actionOnGoodResponse = (json) => {
      // update Redux store
      ReduxLoader.addOrUpdateNotesOnReferrals([json]);
      rerenderFunc();
    };
    apiFetch(null, request, actionOnGoodResponse);
  }

  const createNoteApiStruct = (noteText) => {
    const now = DateTime.local();
    const authorEmail = props.userInfo.email;
    const viewingFirmId = DA.getFirmId();
    return {
      date: now.toISO(),
      authorEmail,
      viewingFirmId,
      referralId: referral.referralId,
      content: noteText,
    };
  };

  const goToReferreeCasePage = () => {
    props.setCaseIntendedClientId(referral.referreeId);
    setRedirect('case');
  };

  function hierarchyValuesAreIncomplete() {
    if (chosenReferralCategory === 'Company / Organization') {
      return chosenReferrerContactId <= 0;
    } else if (chosenReferralCategory === 'Unknown') {
      return false;
    } else if (chosenReferralCategory === 'Word of Mouth') {
      return !chosenWordOfMouthOption;
    } else if (chosenReferralCategory === 'Social Media') {
      return !chosenSocialMediaOption;
    } else if (chosenReferralCategory === 'Media') {
      return !chosenMediaOption;
    }
    // remaining cases are just 'Internet Search' and 'Marketing....'. these are complete in any case
    return false;
  }

  function allHierarchyValuesAreSaved() {
    return (
      chosenReferralCategory === savedSourceCategory &&
      chosenReferrerType === savedReferrerType &&
      chosenReferrerOrgId === savedReferrerOrgId &&
      chosenReferrerContactId === savedReferrerContactId &&
      referralOptionalSpecies === savedOptionalSpecies &&
      chosenWordOfMouthOption === savedWordOfMouthOption &&
      chosenSocialMediaOption === savedSocialMediaOption &&
      chosenMediaOption === savedMediaOption
    );
  }

  function saveButtonShouldBeDisabled() {
    return hierarchyValuesAreIncomplete();
  }

  const styleForHierarchyBeingEdited = {
    padding: '0 18px 14px 18px',
    border: '1px solid red',
  };

  const styleForSavedHierarchy = {
    padding: '0 18px 14px 18px',
  };

  const styleForInvisibleButtonsDiv = {
    display: 'none',
    marginTop: '20px',
  };

  const styleForActiveButtonsDiv = {
    display: 'flex',
    marginTop: '20px',
  };

  const styleForDisabledButtonsDiv = styleForActiveButtonsDiv;

  const styleForButtonsDiv = hierarchyValuesAreIncomplete()
    ? styleForDisabledButtonsDiv
    : allHierarchyValuesAreSaved()
    ? styleForInvisibleButtonsDiv
    : styleForActiveButtonsDiv;

  if (redirect) {
    return <Redirect to={redirect} push />;
  }

  renderIsResultOfWebSocketUpdate = false;

  return (
    <div>
      <Grid container>
        <Grid item style={{ minWidth: '800px', maxWidth: '1200px' }}>
          <Card>
            <CardHeader color="allevaOrange" icon>
              <CardIcon color="allevaOrange">
                <PhoneIcon />
              </CardIcon>
              <div style={{ fontWeight: 700, fontSize: '24px', color: 'black', marginTop: '17px' }}>Referral</div>
            </CardHeader>
            <CardBody>
              <Grid container>
                <Grid item xs={12}>
                  <div style={{ fontSize: '22px', padding: '10px 20px' }}>
                    Incoming Referral of&nbsp;
                    <span onClick={() => goToReferreeCasePage()} style={{ fontWeight: 700, cursor: 'pointer' }}>
                      {referree.preferredFullName}
                    </span>
                  </div>
                </Grid>
                <Grid item xs={4}>
                  <div style={{ marginTop: '-4px', padding: '10px 20px' }}>
                    <KDatePicker
                      label="Referral Date"
                      value={DateTime.fromISO(dateIso)}
                      onChange={(newDate) => handleDateChange(newDate, props)}
                    />
                  </div>
                </Grid>
                <Grid item xs={4}>
                  <div style={{ padding: '10px 20px' }}>
                    <KSelect
                      id="communication-type"
                      name="communicationType"
                      label="Communication Type"
                      fullWidth={true}
                      disabledOption="Choose Type"
                      liveOptions={commTypeDropdownOptions}
                      value={commType}
                      onChange={(event) => handleCommTypeChange(event)}
                    />
                  </div>
                </Grid>
                <Grid item xs={4}>
                  <div style={{ padding: '10px 20px' }}>
                    <KSelect
                      id="receiver-selector"
                      label="Received By"
                      fullWidth={true}
                      disabledOption="Choose Receiver"
                      liveOptions={receiverDropdownOptions.map((o) => o.label)}
                      liveValues={receiverDropdownOptions.map((o) => o.value)}
                      value={receiverId}
                      onChange={(event) => hanldeReceiverChange(event)}
                    />
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <div style={allHierarchyValuesAreSaved() ? styleForSavedHierarchy : styleForHierarchyBeingEdited}>
                    <ReferralSelectionHierarchy
                      searchableContactData={searchableContactData}
                      chosenReferralCategory={chosenReferralCategory}
                      companyType={chosenReferrerType}
                      companyId={chosenReferrerOrgId}
                      contactId={chosenReferrerContactId}
                      chosenWordOfMouthOption={chosenWordOfMouthOption}
                      chosenSocialMediaOption={chosenSocialMediaOption}
                      chosenMediaOption={chosenMediaOption}
                      referralOptionalSpecies={referralOptionalSpecies}
                      onReferralCategoryChange={(referralCategory) => handleReferralCategoryChange(referralCategory)}
                      onCompanyTypeChange={(referrerType) => handleReferrerTypeChange(referrerType)}
                      onCompanyChange={(referrerType, referrerOrgId) =>
                        handleSelectCompany(referrerType, referrerOrgId)
                      }
                      onContactChange={(referrerType, referrerOrgId, personId) =>
                        handleSelectContact(referrerType, referrerOrgId, personId)
                      }
                      onWordOfMouthOptionChange={(newChoice) => handleWordOfMouthOptionChange(newChoice)}
                      onSocialMediaOptionChange={(newChoice) => handleSocialMediaOptionChange(newChoice)}
                      onMediaOptionChange={(newChoice) => handleMediaOptionChange(newChoice)}
                      onOptionalSpeciesChange={(newValue) => setReferralOptionalSpecies(newValue)}
                      onCreateNewCompany={(referrerType) => forceShowingOfNewReferrerCompanyModal(referrerType)}
                      onCreateNewContact={(referrerType, firmId) => forceShowingOfNewContactModal(referrerType, firmId)}
                    />
                    <Grid container style={styleForButtonsDiv}>
                      <Grid item xs={10} style={{ display: 'flex' }}>
                        {' '}
                        &nbsp;{' '}
                      </Grid>
                      <Grid item xs={2} style={{ display: 'flex', float: 'right' }}>
                        <CrmButton outlined onClick={() => cancelReferralSourceInfoChanges()}>
                          Cancel
                        </CrmButton>
                        <CrmButton
                          filled
                          disabled={saveButtonShouldBeDisabled()}
                          onClick={() => saveReferralSourceInfo()}
                        >
                          Save
                        </CrmButton>
                      </Grid>
                    </Grid>
                  </div>
                </Grid>
                <Grid item xs={9}>
                  <div style={{ padding: '10px 20px' }}>
                    <KSelect
                      id="campaign"
                      label="Campaign"
                      fullWidth={true}
                      disabledOption='Select Campaign (or "None")'
                      liveOptions={['None'].concat(campaigns.map((c) => c.name))}
                      liveValues={[0].concat(campaigns.map((c) => c.campaignId))}
                      value={campaignId}
                      onChange={(event) => handleCampaignChange(event)}
                    />
                  </div>
                </Grid>
                <Grid item xs={3}>
                  <div style={{ padding: '10px 20px' }}>
                    <KSelect
                      id="sales-stage"
                      label="Initial Sales Stage"
                      fullWidth={true}
                      disabled={true}
                      disabledOption="Choose Stage"
                      liveOptions={salesStageDropdownOptions}
                      value={salesStage}
                      onChange={(event) => handleInitialSalesStageChange(event)}
                    />
                  </div>
                </Grid>
                {false && (
                  <Grid item xs={12} style={{ background: '#ffeeee' }}>
                    <div style={{ padding: '10px 20px' }}>Substances of Concern</div>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <div style={{ padding: '10px 20px' }}>
                    <NoteAssocGroup
                      notes={notes}
                      newNoteUiFunc={() => setShowNoteModal(true)}
                      pageRerenderFunc={rerenderFunc}
                    />
                  </div>
                </Grid>
              </Grid>
            </CardBody>
          </Card>
        </Grid>
      </Grid>

      <NewNoteModal
        show={showNoteModal}
        noteId={0}
        noteCreationFunc={(text) => createNote(text)}
        closeModalFunc={() => setShowNoteModal(false)}
      />

      <CrmSnackbar show={shouldShowSnackbar} message={snackbarMessage} closeFunc={() => setShouldShowSnackbar(false)} />

      {shouldShowReferrerFirmModal && (
        <IcfReferrerFirmModal
          firmType={chosenReferrerType}
          onSuccess={(visibleFirmType, visibleFirmId, contactId) =>
            handleNewReferrerFirmSuccess(visibleFirmType, visibleFirmId, contactId)
          }
          onCancel={() => setShouldShowReferrerFirmModal(false)}
        />
      )}

      {shouldShowProprietorshipModal && (
        <IcfConsultancyModal
          proprietorshipType={chosenReferrerType}
          onSuccess={(visibleFirmType, visibleFirmId, contactId) =>
            handleNewConsultancySuccess(visibleFirmType, visibleFirmId, contactId)
          }
          onCancel={() => setShouldShowProprietorshipModal(false)}
        />
      )}

      {shouldShowPractitionerModal && (
        <IcfPractitionerModal
          firmType={chosenReferrerType}
          onSuccess={(visibleFirmType, visibleFirmId, contactId) =>
            handleNewPractitionerSuccess(visibleFirmType, visibleFirmId, contactId)
          }
          onCancel={() => setShouldShowPractitionerModal(false)}
        />
      )}

      {shouldShowContactModal && (
        <IcfOrgContactModal
          firmId={chosenReferrerOrgId}
          onSuccess={(contactId) => handleNewOrgContactSuccess(contactId)}
          onCancel={() => setShouldShowContactModal(false)}
        />
      )}
    </div>
  );
}

const mapStateToProps = (state) => ({
  referralId: state.intendedInfo.intendedReferralId,
  firmInfo: state.firmInfo,
  userInfo: state.userInfo,
});

const actionProducers = {
  updateReferral,
  setCaseIntendedClientId,
  clearTaskAccomplishmentInfo,
  setApiResponseInfo,
};

export default connect(mapStateToProps, actionProducers)(ReferralPage);
